import { Helmet } from '@dr.pogodin/react-helmet'
import type { CompositionContextValue } from '@joyful/utils'
import { createNonNullableContext } from '@joyful/utils'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'

import type { AgentChatStatus } from '../../core'
import { isAgentChatInFlight } from '../../core'
import { useDocumentTitle } from './PageTitle'

const SPINNER_FRAMES = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏']
const SPINNER_INTERVAL_MS = 80
const NOTIFICATION_DOT_COLOR = '#3B82F6'

interface GlobalAgentStreamingState {
  readonly hasUnseenCompletion: boolean
  readonly isStreaming: boolean
}

interface GlobalAgentStreamingActions {
  readonly dismissCompletion: () => void
  readonly notifyCompletion: () => void
  readonly registerChatStatus: (id: string, status: AgentChatStatus) => void
  readonly unregisterChat: (id: string) => void
}

type GlobalAgentStreamingContextValue = CompositionContextValue<
  GlobalAgentStreamingState,
  GlobalAgentStreamingActions
>

const GlobalAgentStreamingContext =
  createNonNullableContext<GlobalAgentStreamingContextValue>(
    'GlobalAgentStreamingContext',
  )

export const useGlobalAgentStreaming =
  GlobalAgentStreamingContext.useRequiredContext

export interface GlobalAgentStreamingProviderProps {
  readonly children: ReactNode
}

export function GlobalAgentStreamingProvider({
  children,
}: GlobalAgentStreamingProviderProps) {
  const chatStatusesRef = useRef<Map<string, AgentChatStatus>>(new Map())
  const [isStreaming, setIsStreaming] = useState(false)
  const [hasUnseenCompletion, setHasUnseenCompletion] = useState(false)

  const syncStreamingState = (statuses: Map<string, AgentChatStatus>) => {
    const anyStreaming = Array.from(statuses.values()).some((status) => {
      return isAgentChatInFlight(status)
    })
    setIsStreaming(anyStreaming)
  }

  const registerChatStatus = (id: string, status: AgentChatStatus) => {
    chatStatusesRef.current.set(id, status)
    syncStreamingState(chatStatusesRef.current)
  }

  const unregisterChat = (id: string) => {
    chatStatusesRef.current.delete(id)
    syncStreamingState(chatStatusesRef.current)
  }

  const notifyCompletion = () => {
    if (document.hidden) {
      setHasUnseenCompletion(true)
    }
  }

  const dismissCompletion = () => {
    setHasUnseenCompletion(false)
  }

  return (
    <GlobalAgentStreamingContext.Context
      value={{
        actions: {
          dismissCompletion,
          notifyCompletion,
          registerChatStatus,
          unregisterChat,
        },
        state: { hasUnseenCompletion, isStreaming },
      }}
    >
      {children}
    </GlobalAgentStreamingContext.Context>
  )
}

export function AgentTabNotification() {
  const { notificationTitle } = useAgentTabNotification()

  return notificationTitle ? (
    <Helmet defer={false} title={notificationTitle} />
  ) : null
}

function useAgentTabNotification() {
  const globalAgentStreamingContext = useGlobalAgentStreaming()
  const dismissCompletion =
    globalAgentStreamingContext.actions.dismissCompletion
  const documentTitleContext = useDocumentTitle()
  const [spinnerFrame, setSpinnerFrame] = useState(0)
  const isStreaming = globalAgentStreamingContext.state.isStreaming
  const hasUnseenCompletion =
    globalAgentStreamingContext.state.hasUnseenCompletion

  // oxlint-disable-next-line joyful/no-use-effect -- tab title spinner syncs to document while any chat streams
  useEffect(() => {
    if (!isStreaming) {
      return
    }

    const intervalId = createAgentTabSpinnerInterval(setSpinnerFrame)

    return () => {
      clearInterval(intervalId)
    }
  }, [isStreaming])

  // oxlint-disable-next-line joyful/no-use-effect -- favicon badge and visibility listener are browser sync boundaries
  useEffect(() => {
    return syncAgentTabFaviconNotification({
      dismissCompletion,
      hasUnseenCompletion,
    })
  }, [dismissCompletion, hasUnseenCompletion])

  const spinnerIndex = spinnerFrame % SPINNER_FRAMES.length
  const spinner = SPINNER_FRAMES.at(spinnerIndex) ?? '⠋'

  return {
    notificationTitle: globalAgentStreamingContext.state.isStreaming
      ? `${spinner} ${documentTitleContext.state.pageTitle}`
      : null,
  }
}

function getFaviconLink(size: number): HTMLLinkElement | null {
  return (
    document.querySelector<HTMLLinkElement>(
      `link[rel="icon"][sizes="${size}x${size}"]`,
    ) ?? document.querySelector<HTMLLinkElement>(`link[rel="icon"]`)
  )
}

function loadFaviconImage(href: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      resolve(img)
    }
    img.onerror = reject
    img.src = href
  })
}

function drawFaviconWithDot(img: HTMLImageElement, size: number): string {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return ''
  }

  ctx.drawImage(img, 0, 0, size, size)

  const r = size * 0.22
  const x = size - r - 1
  const y = r + 1

  ctx.beginPath()
  ctx.arc(x, y, r + 1.5, 0, Math.PI * 2)
  ctx.fillStyle = '#ffffff'
  ctx.fill()

  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.fillStyle = NOTIFICATION_DOT_COLOR
  ctx.fill()

  return canvas.toDataURL('image/png')
}

async function applyNotificationDot(shouldApply: () => boolean) {
  for (const size of [32, 16]) {
    const link = getFaviconLink(size)
    if (!link) {
      continue
    }

    const original = link.dataset.originalHref ?? link.href
    link.dataset.originalHref = original
    try {
      const imgEl = await loadFaviconImage(original)
      const dataUrl = drawFaviconWithDot(imgEl, size)
      if (dataUrl && shouldApply()) {
        link.href = dataUrl
      }
    } catch {
      if (shouldApply()) {
        link.href = original
      }
    }
  }
}

function restoreFavicon() {
  for (const size of [32, 16]) {
    const link = getFaviconLink(size)
    if (link?.dataset.originalHref) {
      link.href = link.dataset.originalHref
      delete link.dataset.originalHref
    }
  }
}

function createAgentTabSpinnerInterval(
  setSpinnerFrame: (update: (currentSpinnerFrame: number) => number) => void,
) {
  return setInterval(() => {
    setSpinnerFrame((currentSpinnerFrame) => {
      return currentSpinnerFrame + 1
    })
  }, SPINNER_INTERVAL_MS)
}

function syncAgentTabFaviconNotification({
  dismissCompletion,
  hasUnseenCompletion,
}: {
  readonly dismissCompletion: () => void
  readonly hasUnseenCompletion: boolean
}) {
  let shouldApplyNotificationDot = true
  if (hasUnseenCompletion) {
    void applyNotificationDot(() => {
      return shouldApplyNotificationDot
    })
  } else {
    restoreFavicon()
  }

  const handleVisibilityChange = () => {
    if (!document.hidden) {
      shouldApplyNotificationDot = false
      restoreFavicon()
      dismissCompletion()
    }
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)
  return () => {
    shouldApplyNotificationDot = false
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  }
}
